import React, { useState, useEffect } from "react";
import { Save, Loader2, Globe, Layout, Upload, Plus, Trash2, Shield, Sword, Users, Swords, Gift, Scale, Heart, Megaphone, Zap, HelpCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";
const SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL || "http://localhost:3001";

// Defined at module level to avoid re-creation on every render (which would kill input focus)
const SectionCard = ({ icon: Icon, title, onAdd, addLabel, children }) => (
  <Card className="bg-[#1a1a1a]/80 border-[#8B0000]/30 overflow-hidden">
    <CardHeader className="bg-black/20 border-b border-[#8B0000]/10">
      <CardTitle className="text-lg flex items-center justify-between uppercase tracking-widest font-bold text-white">
        <span className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#8B0000]" />
          {title}
        </span>
        {onAdd && (
          <Button size="sm" onClick={onAdd} className="bg-[#8B0000]/20 hover:bg-[#8B0000]/40 text-white border border-[#8B0000]/40 text-xs normal-case tracking-normal">
            <Plus className="w-3 h-3 mr-1" /> {addLabel}
          </Button>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent className="p-6 space-y-6">{children}</CardContent>
  </Card>
);

const DeleteBtn = ({ onClick }) => (
  <Button size="sm" variant="ghost" onClick={onClick} className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-7 w-7 p-0">
    <Trash2 className="w-3.5 h-3.5" />
  </Button>
);

const DEFAULT_HOME_CONTENT = {
  hero: {
    title: "Knight Arena",
    subtitle: "Play your part and rise to become a legend in a dark world of kings, knights, politics and war.",
    ctaPrimary: "Join the Alpha",
    ctaSecondary: "Explore Features"
  },
  stats: [
    { icon: "Users", label: "Alpha Access", sublabel: "Register Now" },
    { icon: "Swords", label: "PvP Combat", sublabel: "Deep & Tactical" },
    { icon: "Gift", label: "Free to Play", sublabel: "No Pay to Win" },
    { icon: "Scale", label: "Player Driven", sublabel: "Politics & War" }
  ],
  paths: {
    title: "Choose Your Path",
    description: "Pick your faction and class to forge your legend in the realm.",
    items: [
      {
        id: "nobility",
        name: "Nobility",
        pathLabel: "Path of",
        description: "Walk the path of honor, power and political intrigue.",
        image: "https://knightarena.sirv.com/Nobility.png",
        tags: ["Politics", "Leadership", "Honor"]
      },
      {
        id: "criminality",
        name: "Criminality",
        pathLabel: "Path of",
        description: "Thrive in the shadows of the realm.",
        image: "https://knightarena.sirv.com/Criminality.png",
        tags: ["Stealth", "Cunning", "Underworld"]
      }
    ]
  },
  factions: {
    title: "Factions",
    description: "Choose your allegiance and shape the political landscape of the realm.",
    items: [
      { name: "House Kalivant", desc: "Noble and honorable, House Kalivant stands for justice and tradition.", color: "from-[#1a1a2e]/80 to-[#0a0a1a]/80" },
      { name: "House Drakonis", desc: "Ambitious and ruthless, House Drakonis seeks dominion through strength.", color: "from-[#1a0a0a]/80 to-[#0a0505]/80" },
      { name: "The Raveners", desc: "Outlaws and anarchists who reject all noble authority.", color: "from-[#0a0a0a]/80 to-[#0a0505]/80" }
    ]
  },
  classes: {
    title: "Classes",
    description: "Each class offers unique playstyles, multiple specializations, and endless depth for mastery.",
    items: [
      {
        name: "Warlord",
        icon: "⚔️",
        description: "The backbone of any army.",
        color: "from-[#1a1a1a]/80 to-[#0a0a0a]/80",
        playStyles: ["Heavy armor frontline", "Mounted cavalry charges"],
        strengths: ["Highest survivability", "Crowd control"]
      },
      {
        name: "Rogue",
        icon: "🗡️",
        description: "Masters of stealth and subterfuge.",
        color: "from-[#1a1a1a]/80 to-[#0a0a0a]/80",
        playStyles: ["Stealth assassination", "Intelligence gathering"],
        strengths: ["Burst damage", "Evasion & escape"]
      },
      {
        name: "Cleric",
        icon: "✨",
        description: "Healers who keep the line standing.",
        color: "from-[#1a1a1a]/80 to-[#0a0a0a]/80",
        playStyles: ["Group healing", "Buff & aura management"],
        strengths: ["Healing & recovery", "Party-wide buffs"]
      },
      {
        name: "Wizard",
        icon: "🔮",
        description: "Masters of raw arcane power.",
        color: "from-[#1a1a1a]/80 to-[#0a0a0a]/80",
        playStyles: ["Area-of-effect damage", "Crowd control & zoning"],
        strengths: ["Highest AoE damage", "Battlefield zoning"]
      }
    ]
  },
  philosophy: {
    title: "Built Different",
    subtitle: "We're a small team of passionate developers creating the MMORPG we've always wanted.",
    items: [
      { title: "No Shareholders", description: "100% independent. No investors telling us to monetize harder." },
      { title: "Never Pay to Win", description: "Skill and dedication are the only currencies that matter." },
      { title: "Player Driven", description: "A living, persistent world shaped by actual players." }
    ]
  },
  cta: {
    title: "Ready to Join the War?",
    subtitle: "Not a fluffy theme-park MMO.\nStart as a trainee and fight your way up to legend.",
    buttonText: "Join Now - It's Free"
  }
};

export default function ContentManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const [content, setContent] = useState({});
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const token = localStorage.getItem("ka_admin_token");

  const DEFAULT_FEATURES_CONTENT = {
    hero: { title: "Game Features", subtitle: "Everything you need to know about what makes Knight Arena unique." },
    features: [
      { title: "Intense PvP Combat", description: "Our bread and butter. A combat system designed by PvP enthusiasts, for PvP enthusiasts. Every fight matters, every decision counts." },
      { title: "Four Unique Classes", description: "Rogue, Warlord, Cleric, and Wizard - each with multiple specializations and playstyles. Master your class, dominate the arena." },
      { title: "Political System", description: "Navigate a complex web of alliances, rivalries, and power struggles. Influence the realm through politics, not just combat." },
      { title: "National Points & Arena", description: "Prove your worth in ranked PvP. Earn National Points and Arena Points to climb leaderboards and gain legendary status." },
      { title: "Dungeons", description: "Challenging PvE content with unique mechanics and valuable rewards. Perfect your group coordination and strategy." },
      { title: "Castle Siege War", description: "Epic large-scale battles for strategic strongholds. Coordinate attacks, defend walls, and claim castles for your faction." },
    ]
  };

  const DEFAULT_SUPPORT_CONTENT = {
    hero: { title: "Support Center", subtitle: "Need help? We're here to assist you on your journey." },
    faqs: [
      { question: "When will Knight Arena Alpha be released?", answer: "We're targeting Q2 2026 for alpha testing, with full release estimated for late 2027." },
      { question: "Will Knight Arena be free to play?", answer: "Yes! Knight Arena is 100% free to play. Our store only contains cosmetic items - never pay-to-win." },
      { question: "What platforms will Knight Arena be available on?", answer: "Initially PC only. We're focusing on creating the best possible experience before considering other platforms." },
      { question: "How can I join the alpha testing?", answer: "Register for an account and we'll notify you when alpha testing begins." },
      { question: "What makes Knight Arena different from other MMOs?", answer: "No shareholders, no pay-to-win, intense PvP focus, and a dark world where reputation is earned, not bought." },
    ]
  };

  const getDefaults = (pageKey) => {
    if (pageKey === "home") return DEFAULT_HOME_CONTENT;
    if (pageKey === "features") return DEFAULT_FEATURES_CONTENT;
    if (pageKey === "support") return DEFAULT_SUPPORT_CONTENT;
    return {};
  };

  function deepMerge(base, override) {
    if (!override || typeof override !== "object") return base;
    const result = { ...base };
    for (const key of Object.keys(override)) {
      if (
        override[key] !== null &&
        typeof override[key] === "object" &&
        !Array.isArray(override[key]) &&
        typeof base[key] === "object" &&
        !Array.isArray(base[key])
      ) {
        result[key] = deepMerge(base[key] || {}, override[key]);
      } else {
        result[key] = override[key];
      }
    }
    return result;
  }

  const fetchContent = async (pageKey) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/content/${pageKey}`);
      const data = await response.json();
      const fetched = data.content || {};
      setContent(deepMerge(getDefaults(pageKey), fetched));
    } catch (err) {
      toast.error("Failed to load page content");
      setContent(getDefaults(pageKey));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContent(activePage); }, [activePage]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/content`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pageKey: activePage, content })
      });
      if (!response.ok) throw new Error("Save failed");
      toast.success("Changes saved successfully", {
        description: `${activePage.charAt(0).toUpperCase() + activePage.slice(1)} page content has been updated.`
      });
    } catch (err) {
      toast.error("Failed to save changes", {
        description: "Please check your connection and try again."
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Field updaters ──────────────────────────────────────────────────────────
  const updateField = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const updateHeroField = (field, value) => updateField("hero", { ...(content.hero || {}), [field]: value });

  // Stats
  const updateStatItem = (i, field, value) => {
    const arr = [...(content.stats || [])];
    arr[i] = { ...arr[i], [field]: value };
    updateField("stats", arr);
  };
  const addStat = () => updateField("stats", [...(content.stats || []), { icon: "Users", label: "New Stat", sublabel: "Description" }]);
  const deleteStat = (i) => updateField("stats", (content.stats || []).filter((_, idx) => idx !== i));

  // Paths
  const updatePathItem = (i, field, value) => {
    const arr = [...(content.paths?.items || [])];
    arr[i] = { ...arr[i], [field]: value };
    updateField("paths", { ...content.paths, items: arr });
  };
  const addPath = () => updateField("paths", {
    ...content.paths,
    items: [...(content.paths?.items || []), { id: `path-${Date.now()}`, name: "New Path", pathLabel: "Path of", description: "", image: "", tags: [] }]
  });
  const deletePath = (i) => updateField("paths", { ...content.paths, items: (content.paths?.items || []).filter((_, idx) => idx !== i) });

  // Factions
  const updateFactionItem = (i, field, value) => {
    const arr = [...(content.factions?.items || [])];
    arr[i] = { ...arr[i], [field]: value };
    updateField("factions", { ...content.factions, items: arr });
  };
  const addFaction = () => updateField("factions", {
    ...content.factions,
    items: [...(content.factions?.items || []), { name: "New Faction", desc: "", color: "from-[#1a1a1a]/80 to-[#0a0a0a]/80" }]
  });
  const deleteFaction = (i) => updateField("factions", { ...content.factions, items: (content.factions?.items || []).filter((_, idx) => idx !== i) });

  // Classes
  const updateClassItem = (i, field, value) => {
    const arr = [...(content.classes?.items || [])];
    arr[i] = { ...arr[i], [field]: value };
    updateField("classes", { ...content.classes, items: arr });
  };
  const addClassItem = () => updateField("classes", {
    ...content.classes,
    items: [...(content.classes?.items || []), { name: "New Class", icon: "⚔️", description: "", color: "from-[#1a1a1a]/80 to-[#0a0a0a]/80", playStyles: [], strengths: [] }]
  });
  const deleteClassItem = (i) => updateField("classes", { ...content.classes, items: (content.classes?.items || []).filter((_, idx) => idx !== i) });

  // Features
  const updateFeatureItem = (i, field, value) => {
    const arr = [...(content.features || [])];
    arr[i] = { ...arr[i], [field]: value };
    updateField("features", arr);
  };
  const addFeature = () => updateField("features", [...(content.features || []), { title: "New Feature", description: "" }]);
  const deleteFeature = (i) => updateField("features", (content.features || []).filter((_, idx) => idx !== i));

  // FAQs
  const updateFaqItem = (i, field, value) => {
    const arr = [...(content.faqs || [])];
    arr[i] = { ...arr[i], [field]: value };
    updateField("faqs", arr);
  };
  const addFaq = () => updateField("faqs", [...(content.faqs || []), { question: "New Question?", answer: "" }]);
  const deleteFaq = (i) => updateField("faqs", (content.faqs || []).filter((_, idx) => idx !== i));

  // Philosophy
  const updatePhilosophyItem = (i, field, value) => {
    const arr = [...(content.philosophy?.items || [])];
    arr[i] = { ...arr[i], [field]: value };
    updateField("philosophy", { ...content.philosophy, items: arr });
  };
  const addPhilosophyItem = () => updateField("philosophy", {
    ...content.philosophy,
    items: [...(content.philosophy?.items || []), { title: "New Pillar", description: "" }]
  });
  const deletePhilosophyItem = (i) => updateField("philosophy", { ...content.philosophy, items: (content.philosophy?.items || []).filter((_, idx) => idx !== i) });

  // ── Image upload ─────────────────────────────────────────────────────────────
  const handleImageUpload = async (file, index, type) => {
    if (!file) return;
    setUploadingIdx(`${type}-${index}`);
    const formData = new FormData();
    formData.append("image", file);
    formData.append("title", `${type} ${index + 1} image`);
    try {
      const response = await fetch(`${API_URL}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) throw new Error("Upload failed");
      const data = await response.json();
      const fullUrl = `${SERVER_URL}${data.url}`;
      if (type === "path") updatePathItem(index, "image", fullUrl);
      toast.success("Image uploaded successfully");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingIdx(null);
    }
  };

  const triggerFileInput = (index, type) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => handleImageUpload(e.target.files[0], index, type);
    input.click();
  };

  const StatIcon = ({ iconName }) => {
    const icons = { Users, Swords, Gift, Scale };
    const Icon = icons[iconName] || Users;
    return <Icon className="w-5 h-5 text-[#8B0000]" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold flex items-center gap-2 text-white">
          <Globe className="w-5 h-5 text-[#8B0000]" />
          Page Content Management
        </h3>
        <Button onClick={handleSave} disabled={saving} className="bg-[#8B0000] hover:bg-[#B22222] text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Page tabs */}
      <Tabs value={activePage} onValueChange={setActivePage} className="w-full">
        <TabsList className="bg-white/5 border border-[#8B0000]/20 p-1">
          {["home", "features", "support", "store"].map((page) => (
            <TabsTrigger
              key={page}
              value={page}
              className="text-gray-400 data-[state=active]:bg-[#8B0000] data-[state=active]:text-white data-[state=active]:shadow-none capitalize"
            >
              {page === "home" ? "Homepage" : page === "features" ? "Features" : page === "support" ? "Support" : "Store Info"}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-[#8B0000] animate-spin" />
            </div>
          ) : (
            <div className="space-y-8 pb-20">

              {/* ── HERO ── (home only includes CTA buttons) */}
              <SectionCard icon={Layout} title="Hero Section">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-400">Main Title</label>
                    <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.hero?.title || ""} onChange={(e) => updateHeroField("title", e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-400">Subtitle</label>
                    <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.hero?.subtitle || ""} onChange={(e) => updateHeroField("subtitle", e.target.value)} />
                  </div>
                </div>
                {activePage === "home" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-400">Primary Button (Register)</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.hero?.ctaPrimary || ""} onChange={(e) => updateHeroField("ctaPrimary", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-400">Secondary Button (Features)</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.hero?.ctaSecondary || ""} onChange={(e) => updateHeroField("ctaSecondary", e.target.value)} />
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* ── STATS ── */}
              {activePage === "home" && (
                <SectionCard icon={Users} title="Hero Stat Cards" onAdd={addStat} addLabel="Add Card">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(content.stats || []).map((stat, idx) => (
                      <div key={idx} className="p-4 border border-[#8B0000]/20 rounded-lg bg-black/20 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <StatIcon iconName={stat.icon} />
                            <span className="text-xs font-bold text-[#8B0000] uppercase">Card {idx + 1}</span>
                          </div>
                          <DeleteBtn onClick={() => deleteStat(idx)} />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] text-gray-500 uppercase">Label</label>
                          <Input className="bg-white/5 border-[#8B0000]/30 text-white text-xs h-8" value={stat.label || ""} onChange={(e) => updateStatItem(idx, "label", e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] text-gray-500 uppercase">Sublabel</label>
                          <Input className="bg-white/5 border-[#8B0000]/30 text-white text-xs h-8" value={stat.sublabel || ""} onChange={(e) => updateStatItem(idx, "sublabel", e.target.value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* ── PATHS ── */}
              {activePage === "home" && (
                <SectionCard icon={Shield} title="Choose Your Path" onAdd={addPath} addLabel="Add Path">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Section Title</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.paths?.title || ""} onChange={(e) => updateField("paths", { ...content.paths, title: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Section Description</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.paths?.description || ""} onChange={(e) => updateField("paths", { ...content.paths, description: e.target.value })} />
                    </div>
                  </div>

                  <Separator className="bg-[#8B0000]/10" />

                  <div className="grid md:grid-cols-2 gap-6">
                    {(content.paths?.items || []).map((path, idx) => (
                      <div key={idx} className="p-6 border border-[#8B0000]/20 rounded-lg bg-black/20 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-[#8B0000] uppercase tracking-widest text-sm">Path {idx + 1}: {path.name}</h4>
                          <DeleteBtn onClick={() => deletePath(idx)} />
                        </div>

                        {/* Image upload */}
                        <div className="space-y-2">
                          <label className="text-xs text-gray-500 uppercase">Portrait Image</label>
                          <div className="relative group aspect-video rounded-lg overflow-hidden border border-[#8B0000]/30 bg-black/40">
                            {path.image
                              ? <img src={path.image} alt={path.name} className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex flex-col items-center justify-center text-gray-600 gap-2">
                                  <Upload className="w-8 h-8" />
                                  <span className="text-xs italic">No image uploaded</span>
                                </div>
                            }
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white text-white hover:bg-white/10"
                                onClick={() => triggerFileInput(idx, "path")}
                                disabled={uploadingIdx === `path-${idx}`}
                              >
                                {uploadingIdx === `path-${idx}` ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                {path.image ? "Change Image" : "Upload Image"}
                              </Button>
                            </div>
                          </div>
                          {/* URL input as alternative to upload */}
                          <div className="flex items-center gap-2">
                            <Input
                              className="bg-white/5 border-[#8B0000]/30 text-white text-xs h-8 flex-1"
                              placeholder="Or paste image URL..."
                              value={path.image || ""}
                              onChange={(e) => updatePathItem(idx, "image", e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="space-y-1">
                            <label className="text-xs text-gray-500 uppercase">Title</label>
                            <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={path.name || ""} onChange={(e) => updatePathItem(idx, "name", e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-gray-500 uppercase">Path Label</label>
                            <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={path.pathLabel || ""} onChange={(e) => updatePathItem(idx, "pathLabel", e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-gray-500 uppercase">Description</label>
                            <Textarea className="bg-white/5 border-[#8B0000]/30 text-white h-24" value={path.description || ""} onChange={(e) => updatePathItem(idx, "description", e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* ── FACTIONS ── */}
              {activePage === "home" && (
                <SectionCard icon={Shield} title="Factions" onAdd={addFaction} addLabel="Add Faction">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Section Title</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.factions?.title || ""} onChange={(e) => updateField("factions", { ...content.factions, title: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Section Description</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.factions?.description || ""} onChange={(e) => updateField("factions", { ...content.factions, description: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(content.factions?.items || []).map((faction, idx) => (
                      <div key={idx} className="p-4 border border-[#8B0000]/20 rounded-lg bg-black/20 space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-[#8B0000] uppercase tracking-widest">Faction {idx + 1}</label>
                          <DeleteBtn onClick={() => deleteFaction(idx)} />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-500 uppercase">Name</label>
                          <Input className="bg-white/5 border-[#8B0000]/30 text-white text-xs" value={faction.name || ""} onChange={(e) => updateFactionItem(idx, "name", e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-500 uppercase">Description</label>
                          <Textarea className="bg-white/5 border-[#8B0000]/30 text-white h-24 text-xs" value={faction.desc || ""} onChange={(e) => updateFactionItem(idx, "desc", e.target.value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* ── CLASSES ── */}
              {activePage === "home" && (
                <SectionCard icon={Sword} title="Classes" onAdd={addClassItem} addLabel="Add Class">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Section Title</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.classes?.title || ""} onChange={(e) => updateField("classes", { ...content.classes, title: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Section Description</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.classes?.description || ""} onChange={(e) => updateField("classes", { ...content.classes, description: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {(content.classes?.items || []).map((cls, idx) => (
                      <div key={idx} className="p-6 border border-[#8B0000]/20 rounded-lg bg-black/20 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{cls.icon}</span>
                            <h4 className="font-bold text-white">{cls.name}</h4>
                          </div>
                          <DeleteBtn onClick={() => deleteClassItem(idx)} />
                        </div>
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <label className="text-xs text-gray-500 uppercase">Icon (emoji)</label>
                              <Input className="bg-white/5 border-[#8B0000]/30 text-white text-sm" value={cls.icon || ""} onChange={(e) => updateClassItem(idx, "icon", e.target.value)} />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs text-gray-500 uppercase">Name</label>
                              <Input className="bg-white/5 border-[#8B0000]/30 text-white text-sm" value={cls.name || ""} onChange={(e) => updateClassItem(idx, "name", e.target.value)} />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-gray-500 uppercase">Description</label>
                            <Textarea className="bg-white/5 border-[#8B0000]/30 text-white text-sm h-20" value={cls.description || ""} onChange={(e) => updateClassItem(idx, "description", e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* ── PHILOSOPHY ── */}
              {activePage === "home" && (
                <SectionCard icon={Heart} title='"Built Different" Section' onAdd={addPhilosophyItem} addLabel="Add Pillar">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Section Title</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.philosophy?.title || ""} onChange={(e) => updateField("philosophy", { ...content.philosophy, title: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Section Subtitle</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.philosophy?.subtitle || ""} onChange={(e) => updateField("philosophy", { ...content.philosophy, subtitle: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(content.philosophy?.items || []).map((item, idx) => (
                      <div key={idx} className="p-4 border border-[#8B0000]/20 rounded-lg bg-black/20 space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-bold text-[#8B0000] uppercase tracking-widest">Pillar {idx + 1}</label>
                          <DeleteBtn onClick={() => deletePhilosophyItem(idx)} />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-500 uppercase">Title</label>
                          <Input className="bg-white/5 border-[#8B0000]/30 text-white text-xs" value={item.title || ""} onChange={(e) => updatePhilosophyItem(idx, "title", e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] text-gray-500 uppercase">Description</label>
                          <Textarea className="bg-white/5 border-[#8B0000]/30 text-white h-28 text-xs" value={item.description || ""} onChange={(e) => updatePhilosophyItem(idx, "description", e.target.value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* ── CTA ── */}
              {activePage === "home" && (
                <SectionCard icon={Megaphone} title="Call to Action Section">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">CTA Title</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.cta?.title || ""} onChange={(e) => updateField("cta", { ...content.cta, title: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">CTA Subtitle</label>
                      <Textarea className="bg-white/5 border-[#8B0000]/30 text-white h-20" value={content.cta?.subtitle || ""} onChange={(e) => updateField("cta", { ...content.cta, subtitle: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-gray-400">Button Text</label>
                      <Input className="bg-white/5 border-[#8B0000]/30 text-white" value={content.cta?.buttonText || ""} onChange={(e) => updateField("cta", { ...content.cta, buttonText: e.target.value })} />
                    </div>
                  </div>
                </SectionCard>
              )}

              {/* ── FEATURES PAGE ── */}
              {activePage === "features" && (
                <>
                  <SectionCard icon={Zap} title="Features List" onAdd={addFeature} addLabel="Add Feature">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {(content.features || []).map((feat, idx) => (
                        <div key={idx} className="p-4 border border-[#8B0000]/20 rounded-lg bg-black/20 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-[#8B0000] uppercase tracking-widest">Feature {idx + 1}</label>
                            <DeleteBtn onClick={() => deleteFeature(idx)} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-500 uppercase">Title</label>
                            <Input className="bg-white/5 border-[#8B0000]/30 text-white text-sm" value={feat.title || ""} onChange={(e) => updateFeatureItem(idx, "title", e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-500 uppercase">Description</label>
                            <Textarea className="bg-white/5 border-[#8B0000]/30 text-white text-sm h-20" value={feat.description || ""} onChange={(e) => updateFeatureItem(idx, "description", e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </SectionCard>
                </>
              )}

              {/* ── SUPPORT PAGE ── */}
              {activePage === "support" && (
                <>
                  <SectionCard icon={HelpCircle} title="FAQ Items" onAdd={addFaq} addLabel="Add FAQ">
                    <div className="space-y-4">
                      {(content.faqs || []).map((faq, idx) => (
                        <div key={idx} className="p-4 border border-[#8B0000]/20 rounded-lg bg-black/20 space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-[#8B0000] uppercase tracking-widest">FAQ {idx + 1}</label>
                            <DeleteBtn onClick={() => deleteFaq(idx)} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-500 uppercase">Question</label>
                            <Input className="bg-white/5 border-[#8B0000]/30 text-white text-sm" value={faq.question || ""} onChange={(e) => updateFaqItem(idx, "question", e.target.value)} />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] text-gray-500 uppercase">Answer</label>
                            <Textarea className="bg-white/5 border-[#8B0000]/30 text-white text-sm h-20" value={faq.answer || ""} onChange={(e) => updateFaqItem(idx, "answer", e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </SectionCard>
                </>
              )}

            </div>
          )}
        </div>
      </Tabs>
    </div>
  );
}
